"use client";

import React, { useState, useEffect, useCallback, memo } from "react";
import { Menu, X, MessageCircle } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
const STYLES = {
  skeletonContainer: "flex w-full justify-between items-center px-4",
  skeletonGap: "flex gap-4",
  skeletonItem: "h-4 w-16 bg-muted animate-pulse rounded-md",
  desktopNavLink: "text-sm font-default font-medium text-foreground hover:text-primary transition-colors tracking-wide uppercase",
  mobileNavLink: "text-lg font-default font-medium text-foreground hover:text-primary transition-colors block py-2"
};
const DesktopNav = memo(function DesktopNav() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="222832" className={STYLES.skeletonContainer}>
        <div className={STYLES.skeletonGap}>
          <div className={STYLES.skeletonItem} />
          <div className={STYLES.skeletonItem} />
        </div>
        <div className={STYLES.skeletonGap}>
          <div className={STYLES.skeletonItem} />
          <div className={STYLES.skeletonItem} />
        </div>
      </div>;
  }
  return <>
      <div className="hidden md:flex flex-1 justify-start" />
      <div className="hidden md:flex flex-1 justify-end items-center gap-8">
        <NavigationMenu viewport={false}>
          <NavigationMenuList className="gap-8">
            {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
                <NavigationMenuLink asChild>
                  <Link to={item.href || "#"} className={STYLES.desktopNavLink}>
                    {item.label}
                  </Link>
                </NavigationMenuLink>
              </NavigationMenuItem>)}
          </NavigationMenuList>
        </NavigationMenu>
      </div>
    </>;
});
const MobileNavItems = memo(function MobileNavItems({
  setIsOpen
}: {
  setIsOpen: (v: boolean) => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 py-4">
        <div className="h-8 w-32 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-32 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <div className="flex flex-col gap-4 py-6">
      {menuItems.map((item, i) => <div key={item.id} data-index={i}>
          <Link to={item.href || "#"} className={STYLES.mobileNavLink} onClick={() => setIsOpen(false)}>
            {item.label}
          </Link>
        </div>)}
    </div>;
});
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const handleScroll = useCallback(() => {
    const scrolled = window.scrollY > 20;
    setIsScrolled(scrolled);
  }, []);
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    const debouncedScroll = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(handleScroll, 10);
    };
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) hash = "#" + hashPart;
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    window.addEventListener("scroll", debouncedScroll);
    document.addEventListener("click", handleAnchorClick);
    return () => {
      window.removeEventListener("scroll", debouncedScroll);
      document.removeEventListener("click", handleAnchorClick);
      clearTimeout(timeoutId);
    };
  }, [handleScroll]);
  return <section id="header" className={`sticky top-0 z-50 w-full transition-all duration-300 will-change-[opacity] bg-background text-foreground ${isScrolled ? "bg-background/80 backdrop-blur-lg border-b border-border shadow-sm" : "bg-background border-b border-transparent"}`}>
      <div className="container mx-auto px-6 lg:px-12 h-24 relative flex items-center justify-between">
        <MenuProvider menu_id="470">
          <DesktopNav />
        </MenuProvider>
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
          <Link to="/">
            
          </Link>
        </div>
        <div className="md:hidden flex flex-1 justify-end z-20">
          <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-foreground hover:bg-transparent hover:text-primary" aria-label="Toggle menu">
            {isMobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
          </Button>
        </div>
      </div>
      {isMobileMenuOpen && <div className="absolute top-full left-0 w-full bg-background border-b border-border shadow-lg md:hidden flex flex-col px-6 pb-6 animate-in slide-in-from-top-2 duration-200">
          <MenuProvider menu_id="470">
            <MobileNavItems setIsOpen={setIsMobileMenuOpen} />
          </MenuProvider>
        </div>}
      <div className="fixed bottom-6 right-6 z-50">
        <Link to="https://wa.me/8615186790899" newTab>
          <Button size="lg" className="rounded-full size-14 p-0 bg-[#25D366] hover:bg-[#20BA5C] text-white shadow-lg transition-transform hover:scale-110" aria-label="Chat on WhatsApp">
            <MessageCircle className="size-7 fill-current" />
          </Button>
        </Link>
      </div>
    </section>;
}